import { Brain, Heart, Sparkles } from "lucide-react"
import type { AskDynamoSuggestion } from "~/api/askDynamo"
import { substituteDogName } from "~/utils/substituteDogName"

const QUESTION_ICONS = {
  brain: Brain,
  heart: Heart,
  sparkles: Sparkles,
} as const

interface AskDynamoSuggestionListProps {
  dogName: string
  suggestions: AskDynamoSuggestion[]
  onSelect: (text: string) => void
  disabled?: boolean
}

export function AskDynamoSuggestionList({
  dogName,
  suggestions,
  onSelect,
  disabled = false,
}: AskDynamoSuggestionListProps) {
  if (suggestions.length === 0) return null

  return (
    <section className="mt-6" aria-labelledby="ask-dynamo-suggestions-heading">
      <h2
        id="ask-dynamo-suggestions-heading"
        className="text-sm font-semibold text-[var(--color-text-primary)]"
      >
        Try asking about…
      </h2>
      <ul className="mt-3 flex flex-col gap-2">
        {suggestions.map((suggestion, index) => {
          const Icon = QUESTION_ICONS[suggestion.icon]
          const questionText = substituteDogName(suggestion.text, dogName)
          const hintText = suggestion.hint
            ? substituteDogName(suggestion.hint, dogName)
            : null

          return (
            <li key={index}>
              <button
                type="button"
                onClick={() => onSelect(questionText)}
                disabled={disabled}
                className="flex w-full items-start gap-3 rounded-xl border border-[var(--color-ask-dynamo)]/15 bg-white/80 px-3 py-3 text-left shadow-sm transition hover:border-[var(--color-ask-dynamo)]/30 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Icon
                  size={18}
                  className="mt-0.5 shrink-0 text-[var(--color-ask-dynamo)]"
                  aria-hidden
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-[var(--color-text-primary)]">
                    {questionText}
                  </span>
                  {hintText && (
                    <span className="mt-0.5 block text-xs text-[var(--color-text-secondary)]">
                      {hintText}
                    </span>
                  )}
                </span>
              </button>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
